import { Button, Modal, Typography } from "@mui/material";
import React, { FC, useEffect, useState } from "react";
import ErrorMessage from "src/components/ErrorMessage/ErrorMessage";
import Loader from "src/components/Loader/Loader";
import ModalError from "src/components/ModalError/ModalError";
import { ProductIdToNameMap } from "src/constants/ProductIds";
import { useAppDispatch, useAppSelector } from "src/hooks";
import GlobalStateActions from "src/redux/slices/GlobalStateActions";
import { AccountService } from "src/services";
import { FixMeLater } from "src/types";
import {
    StyledDataGrid,
    StyledFlexContainer,
    StyledFormContainer,
    StyledModalContainer,
    StyledNoDataFound,
} from "../../../../AccountAdmin.styled";
import "./ProductsModal.scss";

interface ProductsModalProps {
    open: boolean;
    onClose: () => void;
    handleSuccess: () => void;
}

const ProductsModal: FC<ProductsModalProps> = ({
    open,
    onClose,
    handleSuccess,
}) => {
    const accountService = AccountService.getInstance();
    const dispatch = useAppDispatch();

    const [error, setError] = useState<Error | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const product: FixMeLater = useAppSelector(
        (state) => state?.Product?.value
    );
    const selectedAccount = useAppSelector(
        (state) => state?.[product?.productName].value?.selectedAccount
    );
    const selectedAccountGroup = useAppSelector(
        (state) => state?.[product?.productName].value?.selectedAccountGroup
    );
    const selectedAccountGroups = useAppSelector(
        (state) => state?.[product?.productName].value?.selectedAccountGroups
    );
    const selectedAccountProducts = useAppSelector(
        (state) => state?.[product?.productName].value?.selectedAccountProducts
    );

    const [selectedAccountGroupProductIds, setSelectedAccountGroupProductIds] =
        useState<number[]>([]);

    useEffect(() => {
        const productIds = selectedAccountGroup?.products?.map(
            (productId) => productId
        );
        setSelectedAccountGroupProductIds(productIds || []);
    }, [selectedAccountGroup]);

    useEffect(() => {
        const fetchSelectedAccountProducts = async () => {
            if (selectedAccount) {
                setIsLoading(true);
                try {
                    const selectedAccountProducts =
                        await accountService.getAccountProducts(
                            selectedAccount?.id
                        );

                    dispatch(
                        GlobalStateActions[
                            product?.productName
                        ].setSelectedAccountProducts(selectedAccountProducts)
                    );
                } catch (error) {
                    setError(error as Error);
                } finally {
                    setIsLoading(false);
                }
            }
        };
        fetchSelectedAccountProducts();
    }, [selectedAccount]);

    const handleSubmit = async () => {
        try {
            setIsLoading(true);

            const updatedSelectedAccountGroup = {
                ...selectedAccountGroup,
                products: selectedAccountGroupProductIds,
            };

            const updatedSelectedAccountGroupResponse =
                await accountService.updateAccountGroupContent(
                    updatedSelectedAccountGroup,
                    "product"
                );

            dispatch(
                GlobalStateActions[
                    product?.productName
                ].setSelectedAccountGroup(updatedSelectedAccountGroupResponse)
            );

            const updatedSelectedAccountGroups = selectedAccountGroups.map(
                (group) =>
                    group.id === updatedSelectedAccountGroupResponse.id
                        ? { ...updatedSelectedAccountGroupResponse }
                        : group
            );

            dispatch(
                GlobalStateActions[
                    product?.productName
                ].setSelectedAccountGroups(updatedSelectedAccountGroups)
            );

            onClose();
            handleSuccess();
        } catch (error) {
            setError(error as Error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        onClose();
    };

    const columns = [
        { field: "id", headerName: "Product Id", width: 100 },
        { field: "name", headerName: "Product Name", width: 200 },
    ];

    const accountProductIds = new Set<number>();
    selectedAccountProducts?.forEach((product) =>
        accountProductIds.add(product?.productId || 0)
    );

    const rows = Array.from(accountProductIds)?.map((productId) => ({
        id: productId,
        name: ProductIdToNameMap.get(productId),
    }));

    if (error)
        return (
            <ErrorMessage
                error={`Error fetching product data: ${error.toString()}`}
            />
        );

    if (!error && isLoading) return <Loader />;

    return (
        <Modal open={open} onClose={onClose}>
            <div>
                <StyledModalContainer>
                    <StyledFormContainer>
                        <Typography variant="h5">
                            Edit Group Products
                        </Typography>

                        {selectedAccountProducts?.length === 0 ? (
                            <StyledNoDataFound>
                                No products found for this account.
                            </StyledNoDataFound>
                        ) : (
                            <div className="products-table-container">
                                <StyledDataGrid
                                    columns={columns}
                                    checkboxSelection
                                    onRowSelectionModelChange={(params) => {
                                        setSelectedAccountGroupProductIds(
                                            params as number[]
                                        );
                                    }}
                                    rows={rows}
                                    rowSelectionModel={
                                        selectedAccountGroupProductIds
                                    }
                                />

                                <StyledFlexContainer justify={"center"}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        onClick={handleSubmit}
                                    >
                                        Update
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outlined"
                                        color="secondary"
                                        onClick={handleCancel}
                                    >
                                        Cancel
                                    </Button>
                                </StyledFlexContainer>
                            </div>
                        )}
                    </StyledFormContainer>
                    {isLoading && <Loader />}
                    {error && <ModalError error={error} />}
                </StyledModalContainer>
            </div>
        </Modal>
    );
};

export default ProductsModal;
